import { createSelector, createSlice } from "@reduxjs/toolkit"
import { ChecklistQuestionSpec } from "@nova/schemas"
import { RootState } from "integrations/redux"

type ChecklistCreationSlice = {
    title: string | null
    description: string | null
    modalOpen: boolean
    generatingAiChecklist: boolean
    questions: ChecklistQuestionSpec[] | null
    formHasChanges: boolean
    titleHasChanges: boolean
    descriptionHasChanges: boolean
}

const initialState: ChecklistCreationSlice = {
    title: null,
    description: null,
    modalOpen: false,
    generatingAiChecklist: false,
    questions: null,
    formHasChanges: false,
    titleHasChanges: false,
    descriptionHasChanges: false,
}

export const checklistCreationSliceName = "checklist-creation" as const

export const {
    actions: {
        setChecklistCreationTitle,
        setChecklistCreationDescription,
        openChecklistCreationModal,
        closeChecklistCreationModal,
        setGeneratingAiChecklist,
        setChecklistCreationQuestions,
        clearChecklistCreationQuestions,
        addChecklistCreationQuestion,
        editChecklistCreationQuestion,
        deleteChecklistCreationQuestion,
        moveChecklistCreationQuestion,
        resetChecklistChangeFlags,
    },
    reducer: ChecklistCreationSliceReducer,
} = createSlice({
    name: checklistCreationSliceName,
    initialState,
    reducers: {
        openChecklistCreationModal: (state) => {
            state.modalOpen = true
        },
        closeChecklistCreationModal: (state) => {
            state.modalOpen = false
        },
        setGeneratingAiChecklist: (state, action: { payload: boolean }) => {
            state.generatingAiChecklist = action.payload
        },
        setChecklistCreationTitle: (state, action: { payload: string | null }) => {
            state.title = action.payload
            state.titleHasChanges = true
        },
        setChecklistCreationDescription: (state, action: { payload: string | null }) => {
            state.description = action.payload
            state.descriptionHasChanges = true
        },
        setChecklistCreationQuestions: (
            state,
            action: { payload: ChecklistQuestionSpec[] | null }
        ) => {
            state.questions = action.payload
        },
        clearChecklistCreationQuestions: (state) => {
            state.questions = null
        },
        addChecklistCreationQuestion: (
            state,
            action: { payload: { question: ChecklistQuestionSpec } }
        ) => {
            const { question } = action.payload
            state.questions = state.questions ? [...state.questions, question] : [question]
            state.formHasChanges = true
        },
        editChecklistCreationQuestion: (
            state,
            action: { payload: { id: string; spec: ChecklistQuestionSpec } }
        ) => {
            if (!state.questions) return
            const { id, spec } = action.payload
            const questionIndex = state.questions?.findIndex((q) => q.id === id)
            if (questionIndex === -1) return
            state.questions = state.questions?.map((q) => {
                if (q.id === id) return spec
                return q
            })
            state.formHasChanges = true
        },
        deleteChecklistCreationQuestion: (state, action: { payload: { id: string } }) => {
            state.questions = state.questions?.filter((q) => q.id !== action.payload.id) ?? null
            state.formHasChanges = true
        },
        moveChecklistCreationQuestion: (
            state,
            action: { payload: { id: string; direction: 1 | -1 } }
        ) => {
            if (!state.questions) return
            const { id, direction } = action.payload
            const questionIndex = state.questions.findIndex((q) => q.id === id)
            if (questionIndex === -1) return
            if (questionIndex === 0 && direction === -1) return
            if (questionIndex === state.questions.length - 1 && direction === 1) return
            const questionToMove = state.questions[questionIndex]
            state.questions[questionIndex] = state.questions[questionIndex + direction]
            state.questions[questionIndex + direction] = questionToMove
            state.formHasChanges = true
        },
        resetChecklistChangeFlags: (state) => {
            state.formHasChanges = false
            state.titleHasChanges = false
            state.descriptionHasChanges = false
        },
    },
})

const root = (state: RootState) => state[checklistCreationSliceName]

export const selectChecklistCreationModalOpen = createSelector([root], (s) => s.modalOpen)
export const selectGeneratingAiChecklist = createSelector([root], (s) => s.generatingAiChecklist)
export const selectChecklistCreationTitle = createSelector([root], (s) => s.title)
export const selectChecklistCreationDescription = createSelector([root], (s) => s.description)
export const selectChecklistCreationQuestions = createSelector([root], (s) => s.questions)
export const selectChecklistFormHasChanges = createSelector([root], (s) => s.formHasChanges)
export const selectChecklistTitleHasChanges = createSelector([root], (s) => s.titleHasChanges)
export const selectChecklistDescriptionHasChanges = createSelector(
    [root],
    (s) => s.descriptionHasChanges
)
